<script setup lang="ts">
import { computed } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { useHistorycalStore } from '@/stores/useHistorycalStore';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, LineController, ScatterController } from 'chart.js';
import { Chart } from 'vue-chartjs';
import { metricOptions, filterByMetric } from '@/functions/historical_data';
import { transformDateToCode, calculateAgeInDays } from '@/functions/utils';
import { useI18n } from 'vue-i18n';

// Register Chart.js plugins
ChartJS.register(Title, Tooltip, Legend, LineController, LineElement, PointElement, LinearScale, CategoryScale, ScatterController);

const dataStore = useDataStore();
const historyStore = useHistorycalStore();

const { t } = useI18n();

const options = metricOptions(t);

// Processed chart data for raw values
const processedChart = computed(() => {
  const startdaycode = dataStore.selectedCulture?.startdaycode?.startday_code || '';
  const historicalData = historyStore.historicalData?.results || [];

  // Filter raw data by selected metric
  const rawData = filterByMetric(historyStore.selectedMetric, historicalData).map(item => {
    const time = new Date(item._time);
    const dateStr = time.toISOString().slice(0, 10);
    const dayCode = transformDateToCode(dateStr);
    const age = calculateAgeInDays(dayCode, startdaycode);
    let value = item._value;
    return { x: age, y: value, time: item._time };
  }).filter(point => !isNaN(point.x) && Number.isFinite(point.y));

  const chartData = {
    datasets: [
      {
        label: `${options.find(opt => opt.value === historyStore.selectedMetric)?.label || historyStore.selectedMetric} (Raw)`,
        data: rawData,
        borderColor: 'rgb(0, 184, 190)',
        backgroundColor: 'rgb(0, 184, 190)',
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: 'rgb(0, 184, 190)',
        pointBorderColor: 'rgb(0, 184, 190)',
        pointBorderWidth: 1,
        showLine: false,
        yAxisID: 'metric',
      },
    ]
  };

  return { chartData };
});

// Chart options
const chartOptions = computed(() => {
  const defaultDatasets = [{ data: [] }, { data: [] }];
  const datasets = processedChart.value?.chartData?.datasets || defaultDatasets;

  const metricValues = datasets[0].data.map((p: ChartPoint) => p.y).filter(val => Number.isFinite(val)) || [];

  const getMin = (arr: number[]) => arr.length ? Math.min(...arr) : 0;
  const getMax = (arr: number[]) => arr.length ? Math.max(...arr) : 1;

  const minMetric = metricValues.length ? parseFloat((getMin(metricValues) - (getMax(metricValues) - getMin(metricValues)) * 0.1).toFixed(2)) : 0;
  const maxMetric = metricValues.length ? parseFloat((getMax(metricValues) + (getMax(metricValues) - getMin(metricValues)) * 0.1).toFixed(2)) : 100;

  return {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems: any) => {
            const item = tooltipItems[0];
            return `Âge : ${item.parsed.x} jours`;
          },
          label: (tooltipItem: any) => {
            const datasetLabel = tooltipItem.dataset.label;
            const yValue = tooltipItem.parsed.y;
            const point = tooltipItem.raw as ChartPoint & { time?: string };
            let displayValue: string | number = yValue;
            const unit = historyStore.selectedMetric === 'temperature' ? 'C°' : '';
            return `${datasetLabel} : ${displayValue} ${unit} ${point.time ? `(${new Date(point.time).toLocaleString()})` : ''}`;
          }
        }
      },
      legend: {
        position: 'top' as const,
        labels: {
          generateLabels: () => [
            {
              text: `${options.find(opt => opt.value === historyStore.selectedMetric)?.label || historyStore.selectedMetric}`,
              fillStyle: 'rgb(0, 184, 190)',
              strokeStyle: 'rgb(0, 184, 190)',
              lineWidth: 3
            },
          ]
        }
      }
    },
    scales: {
      metric: {
        beginAtZero: true,
        title: {
          display: true,
          text: options.find(opt => opt.value === historyStore.selectedMetric)?.label || historyStore.selectedMetric
        },
        min: minMetric,
        max: maxMetric,
        position: 'left' as const,
      },
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Âge (jours)'
        },
        ticks: {
          stepSize: 1
        }
      }
    }
  };
});
</script>

<template>
  <div v-if="dataStore.selectedCulture?.pbr?.numero_pbr?.startsWith('B')">
    <div v-if="historyStore.historicalData && dataStore.selectedCulture" class="chart-wrapper">
      <h3>Données brutes par jour</h3>
      <div class="metric-selector">
        <label>
          <select v-model="historyStore.selectedMetric">
            <option v-for="option in options" :value="option.value" :key="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>
      <div v-if="processedChart.chartData.datasets[0].data.length === 0" class="no-data">
        {{ $t('noHistoricalData') }} {{ dataStore.selectedCulture.pbr.numero_pbr }}
      </div>
      <div v-else class="w-full max-w-3xl mx-auto">
        <Chart :type="'scatter'" :data="processedChart.chartData" :options="chartOptions" />
      </div>
    </div>
  </div>
</template>